import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, KeyRound, ShieldCheck } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BrandMark } from '../components/BrandMark';
import { useAuth } from '../auth/AuthContext';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { provider, startProviderLogin } = useAuth();
  const initialEmail = searchParams.get('email') || '';
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState('');

  const returnUrl = useMemo(() => searchParams.get('returnUrl') || '/login?authState=reset-password', [searchParams]);

  const onContinue = () => {
    const safeEmail = email.trim();
    if (!safeEmail) {
      setError('Please enter your email.');
      return;
    }

    if (!provider?.kinde?.enabled) {
      setError('Password reset is available only in Kinde mode.');
      return;
    }

    setError('');
    const isEmailFormatOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(safeEmail) && safeEmail.length <= 254;
    if (!isEmailFormatOk) {
      setError('Please enter a valid email address.');
      return;
    }
    // We send the user into the Kinde flow with email pre-filled. They complete “Forgot password” on the hosted step.
    startProviderLogin(returnUrl, { loginHint: safeEmail, prompt: 'login', maxAge: 0 });
  };

  return (
    <div className="login-screen">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="login-card"
      >
        <BrandMark />
        <div className="login-title-row">
          <div>
            <h2>Reset password</h2>
            <p className="hint-text">We’ll take you to Kinde to finish the secure reset step.</p>
          </div>
          <motion.div className="icon-chip">
            <ShieldCheck size={18} />
          </motion.div>
        </div>

        <div className="login-form">
          <label htmlFor="resetEmail">Email</label>
          <input
            id="resetEmail"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
          <motion.button type="button" className="primary-button" whileTap={{ scale: 0.98 }} onClick={onContinue}>
            Continue to reset
            <ArrowRight size={16} />
          </motion.button>
          <button type="button" className="text-link" onClick={() => navigate('/login', { replace: true })}>
            Back to sign-in
          </button>
        </div>

        <div className="compact-note">
          <strong>Next step</strong>
          <p className="hint-text">
            On the Kinde screen, select <span className="inline-chip">Forgot password</span> and follow the code-based reset.
          </p>
          <div className="inline-row">
            <KeyRound size={16} className="muted-icon" />
            <span>This step stays hosted by Kinde for security.</span>
          </div>
        </div>

        {error && <p className="error-text">{error}</p>}
      </motion.div>
    </div>
  );
}
